import { InfoCircleOutlined } from "@ant-design/icons";
import { Card, Col, Empty, Row, Statistic, Tooltip, Typography } from "antd";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
} from "chart.js";
import { useMemo } from "react";
import { Bar, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import {
  ICancellationAreaStat,
  ICancellationCauseStat,
  ICancellationGroupStat,
  ICancellationPreviousStatusStat,
  ICancellationReasonTypeStat,
  ICancellationStats,
  IOrderOperationsAnalytics,
  IOrderOperationsComparisonMetric,
} from "../../../models/statistcs/respons";
import styles from "./style.module.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
);

interface IProps {
  cancellations?: IOrderOperationsAnalytics["cancellations"];
  customerCancellation?: ICancellationStats;
  administrativeCancellation?: ICancellationStats;
}

interface ICombinedGroup {
  label: string;
  customerCount: number;
  administrativeCount: number;
}

interface ITopItem {
  label: string;
  count: number;
}

const numberValue = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const countValue = (value: unknown) => numberValue(value) ?? 0;

const getByPath = (source: Record<string, any> | undefined, path: string) =>
  path.split(".").reduce<any>((current, key) => current?.[key], source);

const comparisonMetric = (
  source: Record<string, any> | undefined,
  ...paths: string[]
): IOrderOperationsComparisonMetric | undefined => {
  for (const path of paths) {
    const value = getByPath(source, path);
    if (value && typeof value === "object") return value;
  }

  return undefined;
};

const matrixCount = (row: Record<string, any>) =>
  countValue(row.orderCount ?? row.ordersCount ?? row.count);

const matrixLabel = (row: Record<string, any>, keys: string[]) =>
  keys
    .map((key) => row[key])
    .filter(Boolean)
    .join(" / ") || "-";

const topByOrders = <T extends ICancellationGroupStat>(
  rows: T[] | undefined,
  getLabel: (row: T) => string,
): ITopItem | undefined => {
  const row = [...(rows || [])].sort((a, b) => b.orderCount - a.orderCount)[0];
  return row ? { label: getLabel(row), count: row.orderCount } : undefined;
};

const topCombinedGroup = (rows: ICombinedGroup[]): ITopItem | undefined => {
  const row = rows[0];
  return row
    ? {
        label: row.label,
        count: row.customerCount + row.administrativeCount,
      }
    : undefined;
};

const topCandidate = (...items: Array<ITopItem | undefined>) =>
  items
    .filter((item): item is ITopItem => !!item)
    .sort((a, b) => b.count - a.count)[0];

const chartLabel = (label: string) => {
  const text = label || "-";

  if (text.includes("/")) {
    return text
      .split("/")
      .map((part) => part.trim())
      .filter(Boolean)
      .slice(0, 3)
      .map((part) => (part.length > 24 ? `${part.slice(0, 24)}...` : part));
  }

  if (text.length <= 24) return text;

  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return `${text.slice(0, 24)}...`;

  const lines: string[] = [];
  let current = "";

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > 24 && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) lines.push(current);

  if (lines.length > 2) {
    return [lines[0], `${lines[1]}...`];
  }

  return lines;
};

const periodLabel = (item: {
  date?: string;
  startDate?: string;
  label?: string;
}) => {
  const source = item.startDate || item.date || item.label || "-";
  if (/^\d{4}-\d{2}-\d{2}/.test(source)) return source.slice(5, 10);

  const label = item.label || source;
  return label.length > 12 ? `${label.slice(0, 12)}...` : label;
};

const chartHeight = (rowCount: number, minHeight = 380) =>
  Math.max(minHeight, rowCount * 42 + 96);

const mergeGroups = <T extends ICancellationGroupStat>(
  customerRows: T[] | undefined,
  administrativeRows: T[] | undefined,
  getLabel: (row: T) => string,
) => {
  const map = new Map<string, ICombinedGroup>();

  const add = (
    rows: T[] | undefined,
    field: "customerCount" | "administrativeCount",
  ) => {
    (rows || []).forEach((row) => {
      const label = getLabel(row) || "-";
      const current = map.get(label) || {
        label,
        customerCount: 0,
        administrativeCount: 0,
      };
      current[field] += row.orderCount || 0;
      map.set(label, current);
    });
  };

  add(customerRows, "customerCount");
  add(administrativeRows, "administrativeCount");

  return Array.from(map.values())
    .sort(
      (a, b) =>
        b.customerCount +
        b.administrativeCount -
        (a.customerCount + a.administrativeCount),
    )
    .slice(0, 10);
};

const CancellationAnalyticsOverview = ({
  cancellations,
  customerCancellation,
  administrativeCancellation,
}: IProps) => {
  const { t, i18n } = useTranslation();
  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language],
  );

  const chartTextColor =
    document.body.classList.contains("dark") ||
    document.documentElement.getAttribute("data-theme") === "dark"
      ? "rgba(255,255,255,0.78)"
      : "rgba(0,0,0,0.72)";
  const chartGridColor =
    document.body.classList.contains("dark") ||
    document.documentElement.getAttribute("data-theme") === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.08)";

  const formatNumber = (value: unknown) => {
    const number = numberValue(value);
    return number === null ? "-" : numberFormatter.format(number);
  };

  const formatPercent = (value: unknown) => {
    const number = numberValue(value);
    return number === null ? "-" : `${numberFormatter.format(number)}%`;
  };

  const formatMinutes = (value: unknown) => {
    const number = numberValue(value);
    return number === null
      ? "-"
      : `${numberFormatter.format(number)} ${t("minutes")}`;
  };

  const renderTrendTag = (
    metric: IOrderOperationsComparisonMetric | undefined,
    lowerIsBetter = true,
  ) => {
    const delta = numberValue(metric?.delta);
    if (delta === null || delta === 0) return null;

    const isBetter = lowerIsBetter ? delta < 0 : delta > 0;
    const sign = delta > 0 ? "+" : "";
    const rate = numberValue(metric?.deltaRate);

    return (
      <Tooltip
        title={`${t("comparison_change_tooltip")} ${t(
          "current_period",
        )}: ${formatNumber(metric?.current)} | ${t(
          "previous_period",
        )}: ${formatNumber(metric?.previous)}`}
      >
        <span className={isBetter ? styles.goodTrend : styles.badTrend}>
          {sign}
          {formatNumber(delta)}
          {rate !== null ? ` (${sign}${formatPercent(rate)})` : ""}
        </span>
      </Tooltip>
    );
  };

  const comparisonLabels = [
    t("customer_cancellation_stats"),
    t("administrative_cancellation_stats"),
  ];

  const rateChartData = {
    labels: comparisonLabels,
    datasets: [
      {
        label: t("cancellation_rate"),
        data: [
          customerCancellation?.cancellationRate,
          administrativeCancellation?.cancellationRate,
        ],
        backgroundColor: "rgba(250, 140, 22, 0.42)",
        borderColor: "rgba(250, 140, 22, 0.95)",
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: t("missing_reason"),
        data: [
          customerCancellation?.missingReasonRate,
          administrativeCancellation?.missingReasonRate,
        ],
        backgroundColor: "rgba(245, 34, 45, 0.32)",
        borderColor: "rgba(245, 34, 45, 0.9)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const p90ChartData = {
    labels: comparisonLabels,
    datasets: [
      {
        label: t("p90"),
        data: [
          customerCancellation?.p90CancellationMinutes,
          administrativeCancellation?.p90CancellationMinutes,
        ],
        backgroundColor: "rgba(24, 144, 255, 0.38)",
        borderColor: "rgba(24, 144, 255, 0.95)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: chartTextColor, font: { size: 11 }, boxWidth: 10 },
      },
    },
    scales: {
      x: {
        ticks: {
          color: chartTextColor,
          maxRotation: 0,
          minRotation: 0,
          font: { size: 11 },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { color: chartTextColor, font: { size: 11 } },
        grid: { color: chartGridColor },
      },
    },
  };
  const horizontalBarOptions = {
    ...barOptions,
    indexAxis: "y" as const,
    scales: {
      x: {
        beginAtZero: true,
        ticks: { color: chartTextColor, font: { size: 11 } },
        grid: { color: chartGridColor },
      },
      y: {
        ticks: { color: chartTextColor, autoSkip: false, font: { size: 10 } },
        grid: { display: false },
      },
    },
  };
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          color: chartTextColor,
          boxWidth: 10,
          padding: 12,
          font: { size: 11 },
        },
      },
      tooltip: {
        callbacks: {
          title: (items: any[]) => {
            const item = items?.[0]?.raw?.period;
            return item?.startDate && item?.endDate
              ? `${item.startDate} - ${item.endDate}`
              : items?.[0]?.label || "";
          },
          label: (context: any) => {
            const value = context.raw?.value;
            const isRate = context.dataset?.metricType === "rate";
            return `${context.dataset.label}: ${
              isRate ? formatPercent(value) : formatMinutes(value)
            }`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: chartTextColor,
          maxRotation: 0,
          minRotation: 0,
          maxTicksLimit: 9,
          font: { size: 11 },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { color: chartTextColor, font: { size: 11 } },
        grid: { color: chartGridColor },
      },
    },
  };

  const reasonRows = mergeGroups<ICancellationReasonTypeStat>(
    customerCancellation?.reasonTypes,
    administrativeCancellation?.reasonTypes,
    (row) => row.reasonCancellationTypeLabel,
  );
  const previousStatusRows = mergeGroups<ICancellationPreviousStatusStat>(
    customerCancellation?.previousStatuses,
    administrativeCancellation?.previousStatuses,
    (row) => row.previousStatusLabel,
  );
  const causeRows = (administrativeCancellation?.causes || [])
    .slice()
    .sort((a, b) => b.orderCount - a.orderCount)
    .slice(0, 10);

  const groupedChartData = (rows: ICombinedGroup[]) => ({
    labels: rows.map((row) => chartLabel(row.label)),
    datasets: [
      {
        label: t("customer_cancellation_stats"),
        data: rows.map((row) => row.customerCount),
        backgroundColor: "rgba(250, 140, 22, 0.38)",
        borderColor: "rgba(250, 140, 22, 0.95)",
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: t("administrative_cancellation_stats"),
        data: rows.map((row) => row.administrativeCount),
        backgroundColor: "rgba(245, 34, 45, 0.30)",
        borderColor: "rgba(245, 34, 45, 0.9)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  });

  const causeChartData = {
    labels: causeRows.map((row: ICancellationCauseStat) =>
      chartLabel(row.inferredCauseLabel),
    ),
    datasets: [
      {
        label: t("administrative_cancellation_stats"),
        data: causeRows.map((row: ICancellationCauseStat) => row.orderCount),
        backgroundColor: "rgba(114, 46, 209, 0.34)",
        borderColor: "rgba(114, 46, 209, 0.9)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const topReason = topCombinedGroup(reasonRows);
  const topPreviousStatus = topCombinedGroup(previousStatusRows);
  const topCause = topByOrders(
    administrativeCancellation?.causes,
    (row) => row.inferredCauseLabel,
  );
  const topContext = topCandidate(topCause, topPreviousStatus);
  const cancellationComparison = cancellations?.previousPeriodComparison;
  const customerRateComparison = comparisonMetric(
    cancellationComparison,
    "customerCancellation.cancellationRate",
    "customerCancellationRate",
  );
  const administrativeRateComparison = comparisonMetric(
    cancellationComparison,
    "administrativeCancellation.cancellationRate",
    "administrativeCancellationRate",
  );
  const cancellationTimeSeries = cancellations?.timeSeries || [];
  const cancellationRateTrendData = {
    labels: cancellationTimeSeries.map(periodLabel),
    datasets: [
      {
        label: t("customer_cancellation_stats"),
        metricType: "rate",
        data: cancellationTimeSeries.map((item) => ({
          x: periodLabel(item),
          y: item.customerCancellationRate,
          value: item.customerCancellationRate,
          period: item,
        })),
        borderColor: "rgba(250, 140, 22, 0.95)",
        backgroundColor: "rgba(250, 140, 22, 0.16)",
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.28,
        spanGaps: true,
        parsing: { xAxisKey: "x", yAxisKey: "y" },
      },
      {
        label: t("administrative_cancellation_stats"),
        metricType: "rate",
        data: cancellationTimeSeries.map((item) => ({
          x: periodLabel(item),
          y: item.administrativeCancellationRate,
          value: item.administrativeCancellationRate,
          period: item,
        })),
        borderColor: "rgba(245, 34, 45, 0.9)",
        backgroundColor: "rgba(245, 34, 45, 0.14)",
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.28,
        spanGaps: true,
        parsing: { xAxisKey: "x", yAxisKey: "y" },
      },
    ],
  };
  const cancellationP90TrendData = {
    labels: cancellationTimeSeries.map(periodLabel),
    datasets: [
      {
        label: t("customer_cancellation_stats"),
        data: cancellationTimeSeries.map((item) => ({
          x: periodLabel(item),
          y: item.customerP90CancellationMinutes,
          value: item.customerP90CancellationMinutes,
          period: item,
        })),
        borderColor: "rgba(24, 144, 255, 0.95)",
        backgroundColor: "rgba(24, 144, 255, 0.14)",
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.28,
        spanGaps: true,
        parsing: { xAxisKey: "x", yAxisKey: "y" },
      },
      {
        label: t("administrative_cancellation_stats"),
        data: cancellationTimeSeries.map((item) => ({
          x: periodLabel(item),
          y: item.administrativeP90CancellationMinutes,
          value: item.administrativeP90CancellationMinutes,
          period: item,
        })),
        borderColor: "rgba(114, 46, 209, 0.9)",
        backgroundColor: "rgba(114, 46, 209, 0.14)",
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.28,
        spanGaps: true,
        parsing: { xAxisKey: "x", yAxisKey: "y" },
      },
    ],
  };
  const matrixChartData = (
    rows: Record<string, any>[] | undefined,
    labelKeys: string[],
  ) => {
    const sortedRows = (rows || [])
      .slice()
      .sort((a, b) => matrixCount(b) - matrixCount(a))
      .slice(0, 10);

    return {
      rows: sortedRows,
      data: {
        labels: sortedRows.map((row) =>
          chartLabel(matrixLabel(row, labelKeys)),
        ),
        datasets: [
          {
            label: t("orders"),
            data: sortedRows.map(matrixCount),
            backgroundColor: "rgba(19, 194, 194, 0.34)",
            borderColor: "rgba(19, 194, 194, 0.95)",
            borderWidth: 1,
            borderRadius: 4,
          },
        ],
      },
    };
  };
  const areaReasonChart = matrixChartData(
    cancellations?.matrices?.areaReasonMatrix,
    ["areaName", "reasonCancellationTypeLabel", "reasonLabel"],
  );
  const previousStatusReasonChart = matrixChartData(
    cancellations?.matrices?.previousStatusReasonMatrix,
    ["previousStatusLabel", "reasonCancellationTypeLabel", "reasonLabel"],
  );
  const administrativeCauseAreaChart = matrixChartData(
    cancellations?.matrices?.administrativeCauseAreaMatrix,
    ["inferredCauseLabel", "causeLabel", "areaName"],
  );

  const areaMap = new Map<
    string,
    {
      areaName: string;
      customer?: ICancellationAreaStat;
      administrative?: ICancellationAreaStat;
    }
  >();

  const addArea = (
    area: ICancellationAreaStat | undefined,
    field: "customer" | "administrative",
  ) => {
    if (!area) return;
    const key = String(area.areaId ?? area.areaName);
    areaMap.set(key, {
      ...(areaMap.get(key) || {
        areaName: area.areaName || t("outside_areas"),
      }),
      [field]: area,
    });
  };

  (customerCancellation?.areas || []).forEach((area) =>
    addArea(area, "customer"),
  );
  addArea(customerCancellation?.outsideAreas, "customer");
  (administrativeCancellation?.areas || []).forEach((area) =>
    addArea(area, "administrative"),
  );
  addArea(administrativeCancellation?.outsideAreas, "administrative");

  const areaRows = Array.from(areaMap.values())
    .sort(
      (a, b) =>
        countValue(b.customer?.orderCount) +
        countValue(b.administrative?.orderCount) -
        (countValue(a.customer?.orderCount) +
          countValue(a.administrative?.orderCount)),
    )
    .slice(0, 10);
  const areaMax = Math.max(
    0,
    ...areaRows.flatMap((row) => [
      countValue(row.customer?.p90CancellationMinutes),
      countValue(row.administrative?.p90CancellationMinutes),
    ]),
  );

  if (!customerCancellation && !administrativeCancellation) return null;

  return (
    <div className={styles.cancellationSection}>
      <div className={styles.sectionHeader}>
        <Typography.Text strong>{t("cancellation_analytics")}</Typography.Text>
        <Typography.Text type="secondary">
          {t("cancellation_analytics_hint")}
        </Typography.Text>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} xl={6}>
          <Card className={styles.summaryCard} bodyStyle={{ padding: 16 }}>
            <Statistic
              title={t("customer_cancellation_stats")}
              value={formatPercent(customerCancellation?.cancellationRate)}
            />
            {renderTrendTag(customerRateComparison)}
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card className={styles.summaryCard} bodyStyle={{ padding: 16 }}>
            <Statistic
              title={t("administrative_cancellation_stats")}
              value={formatPercent(
                administrativeCancellation?.cancellationRate,
              )}
            />
            {renderTrendTag(administrativeRateComparison)}
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card className={styles.summaryCard} bodyStyle={{ padding: 16 }}>
            <Tooltip title={topReason?.label}>
              <Statistic
                title={t("top_cancellation_reason")}
                value={topReason?.label || "-"}
                suffix={topReason ? formatNumber(topReason.count) : undefined}
              />
            </Tooltip>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card className={styles.summaryCard} bodyStyle={{ padding: 16 }}>
            <Tooltip title={topContext?.label}>
              <Statistic
                title={t("top_cancellation_context")}
                value={topContext?.label || "-"}
                suffix={topContext ? formatNumber(topContext.count) : undefined}
              />
            </Tooltip>
          </Card>
        </Col>

        <Col xs={24} md={12} xl={12}>
          <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
            <div className={styles.sectionHeader}>
              <div className={styles.headingLine}>
                <Typography.Text strong>
                  {t("cancellation_rate_comparison")}
                </Typography.Text>
                <Tooltip title={t("cancellation_rate_comparison_tooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
              <Typography.Text type="secondary">
                {t("cancellation_rate_comparison_hint")}
              </Typography.Text>
            </div>
            <div className={styles.chart}>
              <Bar data={rateChartData} options={barOptions} />
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={12}>
          <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
            <div className={styles.sectionHeader}>
              <div className={styles.headingLine}>
                <Typography.Text strong>
                  {t("cancellation_time_comparison")}
                </Typography.Text>
                <Tooltip title={t("cancellation_time_comparison_tooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
              <Typography.Text type="secondary">
                {t("cancellation_time_comparison_hint")}
              </Typography.Text>
            </div>
            <div className={styles.chart}>
              <Bar data={p90ChartData} options={barOptions} />
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={12}>
          <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
            <div className={styles.sectionHeader}>
              <div className={styles.headingLine}>
                <Typography.Text strong>
                  {t("cancellation_rate_trend")}
                </Typography.Text>
                <Tooltip title={t("cancellation_rate_trend_tooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
              <Typography.Text type="secondary">
                {t("cancellation_rate_trend_hint")}
              </Typography.Text>
            </div>
            <div className={styles.chart}>
              {cancellationTimeSeries.length > 0 ? (
                <Line data={cancellationRateTrendData} options={lineOptions} />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={12}>
          <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
            <div className={styles.sectionHeader}>
              <div className={styles.headingLine}>
                <Typography.Text strong>
                  {t("cancellation_p90_trend")}
                </Typography.Text>
                <Tooltip title={t("cancellation_p90_trend_tooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
              <Typography.Text type="secondary">
                {t("cancellation_p90_trend_hint")}
              </Typography.Text>
            </div>
            <div className={styles.chart}>
              {cancellationTimeSeries.length > 0 ? (
                <Line data={cancellationP90TrendData} options={lineOptions} />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={12}>
          <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
            <div className={styles.sectionHeader}>
              <div className={styles.headingLine}>
                <Typography.Text strong>
                  {t("cancellation_reasons")}
                </Typography.Text>
                <Tooltip title={t("cancellation_reasons_tooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
              <Typography.Text type="secondary">
                {t("cancellation_reasons_hint")}
              </Typography.Text>
            </div>
            <div
              className={`${styles.chart} ${styles.tallChart}`}
              style={{ height: chartHeight(reasonRows.length) }}
            >
              {reasonRows.length > 0 ? (
                <Bar
                  data={groupedChartData(reasonRows)}
                  options={horizontalBarOptions}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={12}>
          <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
            <div className={styles.sectionHeader}>
              <div className={styles.headingLine}>
                <Typography.Text strong>
                  {t("area_reason_matrix")}
                </Typography.Text>
                <Tooltip title={t("area_reason_matrix_tooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
              <Typography.Text type="secondary">
                {t("cancellation_matrix_hint")}
              </Typography.Text>
            </div>
            <div
              className={`${styles.chart} ${styles.tallChart}`}
              style={{ height: chartHeight(areaReasonChart.rows.length) }}
            >
              {areaReasonChart.rows.length > 0 ? (
                <Bar
                  data={areaReasonChart.data}
                  options={horizontalBarOptions}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={12}>
          <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
            <div className={styles.sectionHeader}>
              <div className={styles.headingLine}>
                <Typography.Text strong>
                  {t("previous_status_reason_matrix")}
                </Typography.Text>
                <Tooltip title={t("previous_status_reason_matrix_tooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
              <Typography.Text type="secondary">
                {t("cancellation_matrix_hint")}
              </Typography.Text>
            </div>
            <div
              className={`${styles.chart} ${styles.tallChart}`}
              style={{
                height: chartHeight(previousStatusReasonChart.rows.length),
              }}
            >
              {previousStatusReasonChart.rows.length > 0 ? (
                <Bar
                  data={previousStatusReasonChart.data}
                  options={horizontalBarOptions}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={12}>
          <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
            <div className={styles.sectionHeader}>
              <div className={styles.headingLine}>
                <Typography.Text strong>
                  {t("administrative_cause_area_matrix")}
                </Typography.Text>
                <Tooltip title={t("administrative_cause_area_matrix_tooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
              <Typography.Text type="secondary">
                {t("administrative_cancellation_stats")}
              </Typography.Text>
            </div>
            <div
              className={`${styles.chart} ${styles.tallChart}`}
              style={{
                height: chartHeight(administrativeCauseAreaChart.rows.length),
              }}
            >
              {administrativeCauseAreaChart.rows.length > 0 ? (
                <Bar
                  data={administrativeCauseAreaChart.data}
                  options={horizontalBarOptions}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={12}>
          <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
            <div className={styles.sectionHeader}>
              <div className={styles.headingLine}>
                <Typography.Text strong>
                  {t("previous_statuses")}
                </Typography.Text>
                <Tooltip title={t("previous_statuses_tooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
              <Typography.Text type="secondary">
                {t("top_cancellation_context")}
              </Typography.Text>
            </div>
            <div
              className={`${styles.chart} ${styles.tallChart}`}
              style={{ height: chartHeight(previousStatusRows.length) }}
            >
              {previousStatusRows.length > 0 ? (
                <Bar
                  data={groupedChartData(previousStatusRows)}
                  options={horizontalBarOptions}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={12}>
          <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
            <div className={styles.sectionHeader}>
              <div className={styles.headingLine}>
                <Typography.Text strong>{t("inferred_causes")}</Typography.Text>
                <Tooltip title={t("inferred_causes_tooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
              <Typography.Text type="secondary">
                {t("administrative_cancellation_stats")}
              </Typography.Text>
            </div>
            <div
              className={`${styles.chart} ${styles.tallChart}`}
              style={{ height: chartHeight(causeRows.length) }}
            >
              {causeRows.length > 0 ? (
                <Bar data={causeChartData} options={horizontalBarOptions} />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={12}>
          <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
            <div className={styles.sectionHeader}>
              <div className={styles.headingLine}>
                <Typography.Text strong>
                  {t("cancellation_area_heatmap")}
                </Typography.Text>
                <Tooltip title={t("cancellation_area_heatmap_tooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
              <Typography.Text type="secondary">
                {t("cancellation_area_heatmap_hint")}
              </Typography.Text>
            </div>
            {areaRows.length > 0 ? (
              <div className={styles.cancellationHeatmap}>
                <div className={styles.cancellationHeatmapHeader}>
                  <span>{t("area")}</span>
                  <span>{t("customer_cancellation_stats")}</span>
                  <span>{t("administrative_cancellation_stats")}</span>
                </div>
                {areaRows.map((row) => (
                  <div
                    className={styles.cancellationHeatmapRow}
                    key={row.areaName}
                  >
                    <span className={styles.areaCell}>{row.areaName}</span>
                    {[row.customer, row.administrative].map((item, index) => {
                      const value = numberValue(item?.p90CancellationMinutes);
                      const intensity =
                        value && areaMax > 0
                          ? Math.max(0.08, value / areaMax)
                          : 0;

                      return (
                        <span
                          key={index}
                          className={styles.heatmapCell}
                          style={{
                            backgroundColor:
                              intensity > 0
                                ? `rgba(250, 140, 22, ${0.08 + intensity * 0.42})`
                                : undefined,
                          }}
                        >
                          {formatMinutes(value)}
                        </span>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CancellationAnalyticsOverview;
